"use client"

import {
  createContext,
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react"
import type { UniqueIdentifier } from "@dnd-kit/core"
import {
  pointerWithin,
  rectIntersection,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useDroppable,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from "@dnd-kit/core"
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { cn } from "src/utils/Functions"

export type CardId = string
export type BoardId = string

export type KanbanCard = {
  id: CardId
}

export type KanbanBoard = {
  id: BoardId
  title: string
  cardIds: CardId[]
}

type KanbanContextValue<TCard extends KanbanCard = KanbanCard> = {
  cardsById: Record<CardId, TCard>
  boardsById: Record<BoardId, KanbanBoard>
  boardOrder: BoardId[]
  boardCounts: Record<BoardId, number>
  totalCards: number
  activeCardId: CardId | null
  activeCardWidth: number | null
}

const KanbanContext = createContext<KanbanContextValue<KanbanCard> | null>(null)

function findBoardContainingCard(
  cardId: CardId,
  boards: Record<BoardId, KanbanBoard>,
): BoardId | null {
  for (const [boardId, board] of Object.entries(boards)) {
    if (board.cardIds.includes(cardId)) {
      return boardId
    }
  }

  return null
}

function resolveBoardFromOverId(
  overId: UniqueIdentifier | null | undefined,
  boards: Record<BoardId, KanbanBoard>,
): BoardId | null {
  if (!overId) {
    return null
  }

  const id = String(overId)
  if (id in boards) {
    return id
  }

  return findBoardContainingCard(id, boards)
}

function useKanbanContext<TCard extends KanbanCard = KanbanCard>() {
  const context = useContext(KanbanContext)
  if (!context) {
    throw new Error("Kanban components must be rendered inside KanbanEngineRoot.")
  }

  return context as KanbanContextValue<TCard>
}

export function useKanbanBoard<TCard extends KanbanCard = KanbanCard>() {
  return useKanbanContext<TCard>()
}

export type KanbanEngineRootProps<TCard extends KanbanCard> = {
  cardsById: Record<CardId, TCard>
  initialBoardsById: Record<BoardId, KanbanBoard>
  boardOrder: BoardId[]
  onBoardsChange?: (boardsById: Record<BoardId, KanbanBoard>) => void
  className?: string
  children: ReactNode
}

export function KanbanEngineRoot<TCard extends KanbanCard>({
  cardsById,
  initialBoardsById,
  boardOrder,
  onBoardsChange,
  className,
  children,
}: KanbanEngineRootProps<TCard>) {
  const [boardsById, setBoardsById] =
    useState<Record<BoardId, KanbanBoard>>(initialBoardsById)
  const [activeCardId, setActiveCardId] = useState<CardId | null>(null)
  const [activeCardWidth, setActiveCardWidth] = useState<number | null>(null)
  const suppressClickRef = useRef(false)
  const suppressClickTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  )

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const boardCounts = useMemo(() => {
    const counts = {} as Record<BoardId, number>
    for (const boardId of boardOrder) {
      counts[boardId] = boardsById[boardId]?.cardIds.length ?? 0
    }

    return counts
  }, [boardOrder, boardsById])

  const totalCards = useMemo(
    () =>
      Object.values(boardsById).reduce(
        (sum, board) => sum + board.cardIds.length,
        0,
      ),
    [boardsById],
  )
  const boardIdSet = useMemo(
    () => new Set<BoardId>(Object.keys(boardsById) as BoardId[]),
    [boardsById],
  )
  const collisionDetection = useMemo<CollisionDetection>(
    () => (args) => {
      const pointerCollisions = pointerWithin(args)
      const collisions =
        pointerCollisions.length > 0 ? pointerCollisions : rectIntersection(args)

      if (collisions.length === 0) {
        return closestCenter(args)
      }

      const itemCollisions = collisions.filter(
        (collision) => !boardIdSet.has(String(collision.id)),
      )

      return itemCollisions.length > 0 ? itemCollisions : collisions
    },
    [boardIdSet],
  )

  useEffect(() => {
    return () => {
      if (suppressClickTimeoutRef.current) {
        clearTimeout(suppressClickTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    setBoardsById(initialBoardsById)
  }, [initialBoardsById])

  function suppressNextClick() {
    suppressClickRef.current = true
    if (suppressClickTimeoutRef.current) {
      clearTimeout(suppressClickTimeoutRef.current)
    }

    suppressClickTimeoutRef.current = setTimeout(() => {
      suppressClickRef.current = false
      suppressClickTimeoutRef.current = null
    }, 0)
  }

  function handleDragStart(event: DragStartEvent) {
    setActiveCardId(String(event.active.id))
    const measuredWidth =
      event.active.rect.current.initial?.width ??
      event.active.rect.current.translated?.width
    setActiveCardWidth(typeof measuredWidth === "number" ? measuredWidth : null)
  }

  function handleDragCancel() {
    setActiveCardId(null)
    setActiveCardWidth(null)
    suppressNextClick()
  }

  function handleDragOver(event: DragOverEvent) {
    const { active, over } = event
    if (!over) {
      return
    }

    setBoardsById((previousBoards) => {
      const activeId = String(active.id)
      const overId = String(over.id)

      const sourceBoardId = findBoardContainingCard(activeId, previousBoards)
      const targetBoardId = resolveBoardFromOverId(overId, previousBoards)

      if (!sourceBoardId || !targetBoardId || sourceBoardId === targetBoardId) {
        return previousBoards
      }

      const sourceBoard = previousBoards[sourceBoardId]
      const targetBoard = previousBoards[targetBoardId]
      const sourceIndex = sourceBoard.cardIds.indexOf(activeId)
      if (sourceIndex < 0) {
        return previousBoards
      }

      const nextSourceCardIds = sourceBoard.cardIds.filter((id) => id !== activeId)
      const nextTargetCardIds = targetBoard.cardIds.filter((id) => id !== activeId)

      let insertIndex = nextTargetCardIds.length
      const overIndex = nextTargetCardIds.indexOf(overId)

      if (overIndex >= 0) {
        const activeTop = active.rect.current.translated?.top
        const overMiddleY = over.rect.top + over.rect.height / 2
        const isBelowHoveredCard =
          typeof activeTop === "number" && activeTop > overMiddleY

        insertIndex = overIndex + (isBelowHoveredCard ? 1 : 0)
      }

      const updatedTargetCardIds = [
        ...nextTargetCardIds.slice(0, insertIndex),
        activeId,
        ...nextTargetCardIds.slice(insertIndex),
      ]

      return {
        ...previousBoards,
        [sourceBoardId]: {
          ...sourceBoard,
          cardIds: nextSourceCardIds,
        },
        [targetBoardId]: {
          ...targetBoard,
          cardIds: updatedTargetCardIds,
        },
      }
    })
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    setActiveCardId(null)
    setActiveCardWidth(null)
    suppressNextClick()

    if (!over) {
      return
    }

    setBoardsById((previousBoards) => {
      const activeId = String(active.id)
      const overId = String(over.id)

      const sourceBoardId = findBoardContainingCard(activeId, previousBoards)
      const targetBoardId = resolveBoardFromOverId(overId, previousBoards)

      if (!sourceBoardId || !targetBoardId || sourceBoardId !== targetBoardId) {
        onBoardsChange?.(previousBoards)
        return previousBoards
      }

      const board = previousBoards[sourceBoardId]
      const oldIndex = board.cardIds.indexOf(activeId)
      if (oldIndex < 0) {
        onBoardsChange?.(previousBoards)
        return previousBoards
      }

      const overIndex = board.cardIds.indexOf(overId)
      if (overIndex < 0 || overIndex === oldIndex) {
        onBoardsChange?.(previousBoards)
        return previousBoards
      }

      const updatedBoards = {
        ...previousBoards,
        [sourceBoardId]: {
          ...board,
          cardIds: arrayMove(board.cardIds, oldIndex, overIndex),
        },
      }

      onBoardsChange?.(updatedBoards)
      return updatedBoards
    })
  }

  const contextValue = useMemo(
    () =>
      ({
        cardsById,
        boardsById,
        boardOrder,
        boardCounts,
        totalCards,
        activeCardId,
        activeCardWidth,
      }) as KanbanContextValue<KanbanCard>,
    [
      cardsById,
      boardsById,
      boardOrder,
      boardCounts,
      totalCards,
      activeCardId,
      activeCardWidth,
    ],
  )

  return (
    <KanbanContext.Provider value={contextValue}>
      <div
        className={className}
        onClickCapture={(event) => {
          if (!suppressClickRef.current) {
            return
          }

          event.preventDefault()
          event.stopPropagation()
        }}
      >
        <DndContext
          sensors={sensors}
          collisionDetection={collisionDetection}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          {children}
        </DndContext>
      </div>
    </KanbanContext.Provider>
  )
}

export type KanbanRenderCardProps<TCard extends KanbanCard> = {
  card: TCard
  isDragging: boolean
}

export type KanbanRenderBoardProps<TCard extends KanbanCard> = {
  board: KanbanBoard
  cards: TCard[]
  visibleCardIds: CardId[]
  isOver: boolean
  body: ReactNode
}

type SortableCardProps<TCard extends KanbanCard> = {
  card: TCard
  renderCard: (props: KanbanRenderCardProps<TCard>) => ReactNode
}

function SortableCard<TCard extends KanbanCard>({
  card,
  renderCard,
}: SortableCardProps<TCard>) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({
      id: card.id,
    })

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Translate.toString(transform),
        transition,
      }}
      className={cn(
        "w-full touch-none cursor-grab select-none active:cursor-grabbing",
        isDragging && "z-10",
      )}
      {...attributes}
      {...listeners}
    >
      {renderCard({ card, isDragging })}
    </div>
  )
}

type BoardColumnProps<TCard extends KanbanCard> = {
  board: KanbanBoard
  cardsById: Record<CardId, TCard>
  renderCard: (props: KanbanRenderCardProps<TCard>) => ReactNode
  boardClassName?: string
  boardBodyClassName?: string
  emptyState?: ReactNode
  wrapSortableItem?: boolean
  renderBoard?: (props: KanbanRenderBoardProps<TCard>) => ReactNode
}

function BoardColumn<TCard extends KanbanCard>({
  board,
  cardsById,
  renderCard,
  boardClassName,
  boardBodyClassName,
  emptyState,
  wrapSortableItem = true,
  renderBoard,
}: BoardColumnProps<TCard>) {
  const { isOver, setNodeRef } = useDroppable({
    id: board.id,
  })

  const visibleCardIds = useMemo(
    () => board.cardIds.filter((cardId) => Boolean(cardsById[cardId])),
    [board.cardIds, cardsById],
  )
  const cards = useMemo(
    () => visibleCardIds.map((cardId) => cardsById[cardId]).filter(Boolean) as TCard[],
    [visibleCardIds, cardsById],
  )

  const body = (
    <div
      ref={setNodeRef}
      className={cn(
        "flex min-h-40 flex-1 flex-col gap-3 rounded-xl border border-neutral-100 bg-neutral-50 p-3 transition-colors duration-150",
        isOver && "border-blue-200 bg-blue-50/70",
        boardBodyClassName,
      )}
    >
      <SortableContext items={visibleCardIds} strategy={verticalListSortingStrategy}>
        {cards.map((card) =>
          wrapSortableItem ? (
            <SortableCard key={card.id} card={card} renderCard={renderCard} />
          ) : (
            <Fragment key={card.id}>
              {renderCard({ card, isDragging: false })}
            </Fragment>
          ),
        )}
      </SortableContext>
      {cards.length === 0 ? (
        emptyState ?? (
          <div className="grid min-h-28 place-items-center rounded-lg border border-dashed border-neutral-200 bg-white/70">
            <p className="text-xs text-neutral-400">Drop a card here</p>
          </div>
        )
      ) : null}
    </div>
  )

  if (renderBoard) {
    return renderBoard({
      board,
      cards,
      visibleCardIds,
      isOver,
      body,
    })
  }

  return (
    <section
      className={cn(
        "flex min-w-72 flex-1 flex-col rounded-2xl border border-neutral-100 bg-white p-5 shadow-sm",
        boardClassName,
      )}
    >
      <header className="mb-4 flex items-center justify-between">
        <h2 className="text-sm font-semibold tracking-tight text-neutral-900">
          {board.title}
        </h2>
        <span className="inline-flex items-center rounded-full bg-neutral-100 px-2.5 py-0.5 text-xs font-medium text-neutral-600">
          {board.cardIds.length}
        </span>
      </header>
      {body}
    </section>
  )
}

export type KanbanEngineColumnsProps<TCard extends KanbanCard> = {
  renderCard: (props: KanbanRenderCardProps<TCard>) => ReactNode
  className?: string
  boardClassName?: string
  boardBodyClassName?: string
  emptyState?: ReactNode
  wrapSortableItem?: boolean
  renderBoard?: (props: KanbanRenderBoardProps<TCard>) => ReactNode
}

export function KanbanEngineColumns<TCard extends KanbanCard>({
  renderCard,
  className,
  boardClassName,
  boardBodyClassName,
  emptyState,
  wrapSortableItem = true,
  renderBoard,
}: KanbanEngineColumnsProps<TCard>) {
  const { cardsById, boardsById, boardOrder, activeCardId, activeCardWidth } =
    useKanbanContext<TCard>()

  const activeCard = activeCardId ? cardsById[activeCardId] : null

  return (
    <>
      <div className={cn("grid gap-4 md:grid-cols-2 xl:grid-cols-4", className)}>
        {boardOrder.map((boardId) => {
          const board = boardsById[boardId]
          if (!board) {
            return null
          }

          return (
            <BoardColumn
              key={board.id}
              board={board}
              cardsById={cardsById}
              renderCard={renderCard}
              boardClassName={boardClassName}
              boardBodyClassName={boardBodyClassName}
              emptyState={emptyState}
              wrapSortableItem={wrapSortableItem}
              renderBoard={renderBoard}
            />
          )
        })}
      </div>

      <DragOverlay>
        {activeCard ? (
          <div
            className="pointer-events-none max-w-full"
            style={activeCardWidth ? { width: `${activeCardWidth}px` } : undefined}
          >
            {renderCard({ card: activeCard, isDragging: true })}
          </div>
        ) : null}
      </DragOverlay>
    </>
  )
}

export const KanbanEngine = {
  Root: KanbanEngineRoot,
  Columns: KanbanEngineColumns,
}
